import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatPrice } from '../data/components';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Layers, Plus, Trash2, ExternalLink, ArrowLeft,
  Clock, Package, LogOut
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyDesigns() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    try {
      const r = await axios.get(`${API}/designs`, { withCredentials: true });
      setDesigns(r.data);
    } catch {
      toast.error('Failed to load designs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (designId) => {
    try {
      await axios.delete(`${API}/designs/${designId}`, { withCredentials: true });
      setDesigns(prev => prev.filter(d => d.design_id !== designId));
      toast.success('Design deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleShare = (shareId) => {
    const url = `${window.location.origin}/shared/${shareId}`;
    navigator.clipboard.writeText(url);
    toast.success('Share link copied!');
  };

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="my-designs-page">
      {/* Header */}
      <header className="glass-panel border-b border-white/5" data-testid="designs-header">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/studio')} className="text-slate-400 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Studio
            </Button>
            <div className="w-px h-6 bg-white/10" />
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              <span className="font-chivo font-bold text-white">My Designs</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/10" />}
            <span className="text-sm text-slate-400">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-white" data-testid="designs-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-chivo font-bold text-2xl text-white mb-1">Saved Designs</h1>
            <p className="text-sm text-slate-500">{designs.length} design{designs.length !== 1 ? 's' : ''}</p>
          </div>
          <Button
            data-testid="new-design-btn"
            onClick={() => navigate('/studio')}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            <Plus className="w-4 h-4" /> New Design
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : designs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-600" data-testid="no-designs">
            <Package className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-base mb-2">No designs yet</p>
            <p className="text-sm mb-6">Create your first Urbania interior design</p>
            <Button onClick={() => navigate('/studio')} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus className="w-4 h-4" /> Start Designing
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {designs.map(d => (
              <div
                key={d.design_id}
                data-testid={`design-card-${d.design_id}`}
                className="glass-card rounded-xl p-5 hover:border-blue-500/20 transition-all duration-300 group cursor-pointer"
                onClick={() => navigate(`/studio/${d.design_id}`)}
              >
                {/* Preview area */}
                <div className="h-32 rounded-lg bg-[#0B1120] border border-white/5 mb-4 flex items-center justify-center vehicle-grid-pattern relative overflow-hidden">
                  <span className="text-xs text-slate-600 font-jetbrains">
                    {(d.placed_components || []).length} components
                  </span>
                </div>

                <h3 className="font-chivo font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors truncate">
                  {d.name}
                </h3>

                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="secondary" className="text-[10px] bg-blue-500/10 text-blue-400 border-none">
                    {formatPrice(d.total_cost || 0)}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px] bg-slate-800 text-slate-400 border-none">
                    {d.total_weight || 0}kg
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-slate-600 text-[10px]">
                    <Clock className="w-3 h-3" />
                    {new Date(d.updated_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-blue-400"
                      onClick={(e) => { e.stopPropagation(); handleShare(d.share_id); }} data-testid={`share-design-${d.design_id}`}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500 hover:text-red-400"
                      onClick={(e) => { e.stopPropagation(); handleDelete(d.design_id); }} data-testid={`delete-design-${d.design_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
